import { useEffect, useRef, useState } from 'react';
import { isMonitoringSessionType } from 'components/MonitoringAgent/utils/monitoringSessionFlowUtils';
import { PENDING_ACTION_TTL_MS, useAiScreenAction } from 'contexts/AiScreenActionsContext';

import type { TAiScreenAction } from 'utils/interfaces/IAiScreenAction';
import type {
  IPendingMonitoringSessionOpen,
  IUseValueChainMonitoringSessionAiActionParams,
} from '../interfaces/IUseValueChainMonitoringSessionAiAction';

/**
 * Answers the assistant's `openMonitoringSession` action on the value chain tab: opens a
 * monitoring session's results, of any agent, or the session in the state it is in
 * (`onOpenSession` decides).
 *
 * The chat made or started the session on the backend, so the list the tab holds can be old, or
 * not there yet when the action made the app navigate to the map. The action is kept until a
 * sessions list read after it arrives and the board is laid out; the session then opens as that
 * list has it. A session the list does not hold, or one that is no monitoring session, opens
 * nothing. Same flow as `useValueChainAgentSessionAiAction`.
 */
const useValueChainMonitoringSessionAiAction = ({
  valueChainMapId,
  agentSessions,
  isAgentSessionsFetching,
  agentSessionsFulfilledTimeStamp,
  refetchAgentSessions,
  isBoardReady,
  onOpenSession,
}: IUseValueChainMonitoringSessionAiActionParams): void => {
  const [pending, setPending] = useState<IPendingMonitoringSessionOpen | null>(null);
  const refetchAgentSessionsRef = useRef(refetchAgentSessions);
  refetchAgentSessionsRef.current = refetchAgentSessions;

  useAiScreenAction((action: TAiScreenAction): boolean => {
    if (action.type !== 'openMonitoringSession' || action.valueChainMapId !== Number(valueChainMapId)) {
      return false;
    }

    setPending({
      sessionId: action.sessionId,
      view: action.view,
      executionKey: action.executionKey,
      requestedAt: Date.now(),
    });

    return true;
  });

  useEffect(() => {
    if (pending) {
      Promise.resolve(refetchAgentSessionsRef.current()).catch(() => {});
    }
  }, [pending]);

  useEffect(() => {
    if (!pending) {
      return;
    }

    // A list that never arrives — the read failed, say — must not open the panel much later.
    if (Date.now() - pending.requestedAt > PENDING_ACTION_TTL_MS) {
      setPending(null);
      return;
    }

    if (
      !isBoardReady
      || isAgentSessionsFetching
      || (agentSessionsFulfilledTimeStamp ?? 0) < pending.requestedAt
    ) {
      return;
    }

    setPending(null);

    const session = agentSessions?.find(({ id }) => id === pending.sessionId);

    if (session && isMonitoringSessionType(session.type)) {
      onOpenSession(session, { view: pending.view, executionKey: pending.executionKey });
    }
  }, [
    agentSessions,
    agentSessionsFulfilledTimeStamp,
    isAgentSessionsFetching,
    isBoardReady,
    onOpenSession,
    pending,
  ]);
};

export default useValueChainMonitoringSessionAiAction;
